import React, { useState, useCallback, useEffect } from 'react';
import ContentEditable from 'react-contenteditable';
import { useAuth } from '../hooks/useAuth';
import { useContent } from '../hooks/useContent';
import { Pencil, Check, X } from 'lucide-react';

interface EditableContentProps {
  pageId: string;
  contentKey: string;
  type: 'text' | 'image' | 'list';
  defaultContent: string;
  className?: string;
  tag?: keyof JSX.IntrinsicElements;
}

export function EditableContent({
  pageId,
  contentKey,
  type,
  defaultContent,
  className = '',
  tag = 'div'
}: EditableContentProps) {
  const { isAuthenticated } = useAuth();
  const { language, updateContent, contents } = useContent();
  const [isEditing, setIsEditing] = useState(false);
  const [editedContent, setEditedContent] = useState('');
  
  const content = contents[contentKey]?.[language] || defaultContent;

  useEffect(() => {
    setEditedContent(content);
  }, [content]);

  const handleChange = useCallback((evt: any) => {
    setEditedContent(evt.target.value);
  }, []);

  const handleSave = async () => {
    await updateContent(pageId, contentKey, {
      ...contents[contentKey],
      [language]: editedContent
    }, type);
    setIsEditing(false);
  };

  const handleCancel = () => {
    setEditedContent(content);
    setIsEditing(false);
  };

  if (!isAuthenticated) {
    return React.createElement(tag, { className }, content);
  }
  
  if (!isEditing) {
    return (
      <div className="group relative">
        {React.createElement(tag, { className }, content)}
        <button
          onClick={() => setIsEditing(true)}
          className="absolute -right-8 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-all 
                   hover:scale-110 bg-white shadow-lg rounded-full p-1.5 border border-orange-200
                   hover:border-orange-400 hover:shadow-orange-100"
        >
          <Pencil className="w-3.5 h-3.5 text-orange-500" />
        </button>
      </div>
    );
  }

  return (
    <div className="relative group animate-fadeIn">
      <ContentEditable
        html={String(editedContent || '')}
        onChange={handleChange}
        tagName={tag}
        className={`${className} cursor-text border-b-2 border-orange-400 bg-orange-50/10 
                   px-2 py-1 focus:outline-none transition-all rounded-sm`}
      />
      <div className="absolute -right-20 top-1/2 -translate-y-1/2 flex gap-1.5 animate-slideIn">
        <button
          onClick={handleSave}
          className="bg-white shadow-lg rounded-full p-1.5 border border-green-200
                   hover:border-green-400 hover:shadow-green-100 hover:scale-110 transition-all"
        >
          <Check className="w-3.5 h-3.5 text-green-500" />
        </button>
        <button
          onClick={handleCancel}
          className="bg-white shadow-lg rounded-full p-1.5 border border-red-200
                   hover:border-red-400 hover:shadow-red-100 hover:scale-110 transition-all"
        >
          <X className="w-3.5 h-3.5 text-red-500" />
        </button>
      </div>
    </div>
  );
}